import { tC } from "../../utils/TranslateWithContentLanguage";
import { CheckCircle, RefreshCw } from "lucide-react";
import CardImage from "../tool_components/CardImage";
import { Button, Text } from "@core/index";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { PerformanceTaskResponseDto } from "@packages/api/madlenAPI.schemas";
import PerformanceTaskCardPDF from "./PerformanceTaskCardPdf";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetPerformanceTaskQueryKey,
  getGetUserPerformanceTasksQueryKey,
  updatePerformanceTask,
} from "@packages/api/madlenAPI";

interface ExtendedPerformanceTaskResponseDto
  extends PerformanceTaskResponseDto {
  isEditable?: boolean;
}

export type Tool = ExtendedPerformanceTaskResponseDto;

export type PerformanceTaskCardProps = {
  id: string;
  isImageLoading?: boolean;
  onCreateAgain?: () => void;
  isEditable?: boolean;
  contentLanguage: string;
} & Required<Pick<PerformanceTaskResponseDto, "courseName" | "gradeLevel">> &
  Pick<
    PerformanceTaskResponseDto,
    | "imageUrl"
    | "videoUrl"
    | "performanceCriteria"
    | "prompt"
    | "description"
    | "studentInstructions"
    | "materialsNeeded"
    | "createdAt"
    | "authorName"
  >;

export const PerformanceTaskCard = ({
  id,
  performanceCriteria = [],
  isImageLoading,
  imageUrl,
  videoUrl,
  courseName,
  gradeLevel,
  prompt,
  description,
  studentInstructions,
  materialsNeeded = [],
  createdAt,
  authorName,
  onCreateAgain,
  isEditable = false,
  contentLanguage,
}: PerformanceTaskCardProps) => {
  const finalImageUrl = useImagePdf(imageUrl);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  return (
    <div className="space-y-6">
      <CardImage
        createdAt={createdAt}
        theme="ocean"
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        isLoading={isImageLoading}
        courseName={courseName}
        gradeLevel={gradeLevel}
        topic={
          <div className="flex items-center gap-4 justify-center">
            <div>
              <Text variant="h1" className="mb-2">
                {prompt}
              </Text>
            </div>
          </div>
        }
        contentLanguage={contentLanguage}
      />
      <div className="flex justify-end gap-2 mt-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-cyan-400 to-blue-500 dark:from-cyan-600 dark:to-blue-700 hover:from-cyan-500 hover:to-blue-600 dark:hover:from-cyan-700 dark:hover:to-blue-800"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          pdfTitle={prompt || ""}
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          document={
            <PerformanceTaskCardPDF
              id={id}
              createdAt={createdAt}
              performanceCriteria={performanceCriteria}
              authorName={authorName ?? ""}
              courseName={courseName}
              gradeLevel={gradeLevel}
              prompt={prompt || ""}
              imageUrl={finalImageUrl}
              description={description || ""}
              studentInstructions={studentInstructions || ""}
              materialsNeeded={materialsNeeded}
              customizationSettings={customizationSettings}
            />
          }
          btnTheme="bg-gradient-to-r from-cyan-400 to-blue-500 dark:from-cyan-600 dark:to-blue-700"
        />
      </div>
      <div className="space-y-6">
        {description && (
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900 rounded-xl p-6">
            <h5 className="font-semibold text-lg mb-4 text-gray-900 dark:text-gray-100">
              {tC(contentLanguage, "performanceTasks.description")}
            </h5>
            <div className="prose dark:prose-invert max-w-none">
              <EditableMarkdown
                value={description}
                isEditable={isEditable}
                handleSave={async (newValue) => {
                  await updatePerformanceTask(id, {
                    description: newValue,
                  });

                  await queryClient.invalidateQueries({
                    queryKey: getGetUserPerformanceTasksQueryKey(),
                  });
                  await queryClient.invalidateQueries({
                    queryKey: getGetPerformanceTaskQueryKey(id),
                  });
                }}
              />
            </div>
          </div>
        )}

        {studentInstructions && (
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900 rounded-xl p-6">
            <h5 className="font-semibold text-lg mb-4 text-gray-900 dark:text-gray-100">
              {tC(contentLanguage, "performanceTasks.studentInstructions")}
            </h5>
            <div className="prose dark:prose-invert max-w-none">
              <EditableMarkdown
                value={studentInstructions}
                isEditable={isEditable}
                handleSave={async (newValue) => {
                  await updatePerformanceTask(id, {
                    studentInstructions: newValue,
                  });

                  await queryClient.invalidateQueries({
                    queryKey: getGetUserPerformanceTasksQueryKey(),
                  });
                  await queryClient.invalidateQueries({
                    queryKey: getGetPerformanceTaskQueryKey(id),
                  });
                }}
              />
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900 rounded-xl p-6">
          <h5 className="font-semibold text-lg mb-4 text-gray-900 dark:text-gray-100">
            {tC(contentLanguage, "performanceTasks.performanceCriteria")}
          </h5>
          <div className="max-w-none">
            {performanceCriteria.map((criteria, index) => (
              <div key={index} className="flex items-center gap-2 mb-4">
                <CheckCircle
                  size={20}
                  className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                />
                <div className="flex-1">
                  <EditableMarkdown
                    value={criteria}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      if (!performanceCriteria) return;

                      const updatedCriteria = [...performanceCriteria];
                      updatedCriteria[index] = newValue;

                      await updatePerformanceTask(id, {
                        performanceCriteria: updatedCriteria,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserPerformanceTasksQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetPerformanceTaskQueryKey(id),
                      });
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {materialsNeeded && materialsNeeded.length > 0 && (
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900 rounded-xl p-6">
            <h5 className="font-semibold text-lg mb-4 text-gray-900 dark:text-gray-100">
              {tC(contentLanguage, "performanceTasks.materialsNeeded")}
            </h5>
            <ul className="space-y-2">
              {materialsNeeded.map((material, index) => (
                <li
                  key={index}
                  className="flex items-start justify-start gap-2"
                >
                  <CheckCircle
                    size={20}
                    className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1.5"
                  />
                  <div className="text-gray-700 dark:text-gray-300">
                    <EditableMarkdown
                      value={material}
                      isEditable={isEditable}
                      handleSave={async (newValue) => {
                        if (!materialsNeeded) return;

                        const updatedMaterials = [...materialsNeeded];
                        updatedMaterials[index] = newValue;

                        await updatePerformanceTask(id, {
                          materialsNeeded: updatedMaterials,
                        });

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserPerformanceTasksQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetPerformanceTaskQueryKey(id),
                        });
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <PerformanceTaskCardPDF
            id={id}
            performanceCriteria={performanceCriteria}
            courseName={courseName}
            createdAt={createdAt}
            authorName={authorName ?? ""}
            gradeLevel={gradeLevel}
            prompt={prompt || ""}
            imageUrl={finalImageUrl}
            description={description || ""}
            studentInstructions={studentInstructions || ""}
            materialsNeeded={materialsNeeded}
            customizationSettings={customizationSettings}
          />
        }
      />
    </div>
  );
};
